import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export const formatDateTime = (date: string | number, includeTime: boolean = true): string => {
  const dayjsDate = dayjs(date);

  if (!dayjsDate.isValid()) {
    return '-';
  }

  if (!includeTime) {
    return dayjsDate.format('YYYY-MM-DD');
  }

  return dayjsDate.format('YYYY-MM-DD HH:mm:ss');
};

export const formatDate = (date: string | number): string => {
  return formatDateTime(date, false);
};

export const formatTime = (date: string | number): string => {
  const dayjsDate = dayjs(date);

  if (!dayjsDate.isValid()) {
    return '-';
  }

  return dayjsDate.format('HH:mm:ss');
};

/**
 * 타임스탬프를 HH:mm 형식으로 포맷
 * @param date - 타임스탬프 (문자열 또는 숫자)
 * @returns "HH:mm" 형식 문자열, 유효하지 않은 경우 "-"
 */
export const formatHourMinute = (date: string | number): string => {
  const dayjsDate = dayjs(date);

  if (!dayjsDate.isValid()) {
    return '-';
  }

  return dayjsDate.format('HH:mm');
};

export type DateRange = {
  startDate: string;
  endDate: string;
};

/**
 * 일수를 날짜 범위로 변환
 * @param days - 오늘 기준 며칠 전까지의 범위 (예: 7 → 최근 7일)
 * @param daysBefore - 몇일 전 기준 숫자 받기
 * @returns 시작일과 종료일 (예: { startDate: "2025-12-03", endDate: "2025-12-10" })
 *          잘못된 입력값인 경우 { startDate: "0000-00-00", endDate: "0000-00-00" } 반환
 */
export const formatDaysToDateRange = (days: number, daysBefore: number = 0): DateRange => {
  if (isNaN(days) || !days || days <= 0) {
    return { startDate: '0000-00-00', endDate: '0000-00-00' };
  }

  const endDate = dayjs().subtract(daysBefore, 'day');
  const endDateFormatted = endDate.format('YYYY-MM-DD');
  const startDate = endDate.subtract(days - 1, 'day').format('YYYY-MM-DD');

  return { startDate, endDate: endDateFormatted };
};

/**
 * 날짜 범위를 문자열로 변환
 * @param startDate - 시작일
 * @param endDate - 종료일
 * @returns 날짜 범위 (예: "2025-12-03 ~ 2025-12-10") 또는 "-" (시작일 또는 종료일이 없을 때)
 */
export const formatDateRange = (startDate: string, endDate: string): string => {
  if (!startDate || !endDate) return '-';
  if (startDate === endDate) return startDate;

  return `${startDate} ~ ${endDate}`;
};

export const formatToKstFromUtc = (date: string) => {
  const utcDate = dayjs.utc(date);

  if (!utcDate.isValid()) {
    return '-';
  }

  const kstDate = utcDate.add(9, 'hour');
  const kstDateString = kstDate.format('YYYY-MM-DD HH:mm:ss');
  return kstDateString;
};

/**
 * 시작 시간과 종료 시간을 받아 소요 시간을 포맷팅
 * @param startTime - 시작 시간 (ISO 문자열)
 * @param endTime - 종료 시간 (ISO 문자열)
 * @returns 소요 시간 (예: "2시간 30분", "45분 20초", "30초") 또는 "-" (시작/종료 시간이 없을 때)
 */
export const formatDuration = (startTime?: string, endTime?: string): string => {
  if (!startTime || !endTime) return '-';
  const start = new Date(startTime).getTime();
  const end = new Date(endTime).getTime();
  const diffMs = end - start;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);

  if (diffHour > 0) {
    return `${diffHour}시간 ${diffMin % 60}분`;
  }
  if (diffMin > 0) {
    return `${diffMin}분 ${diffSec % 60}초`;
  }
  return `${diffSec}초`;
};

export const formatDurationSeconds = (totalSeconds?: number | null): string => {
  if (typeof totalSeconds !== 'number' || !Number.isFinite(totalSeconds) || totalSeconds < 0) {
    return '-';
  }

  const normalizedTotalSeconds = Math.floor(totalSeconds);
  const hours = Math.floor(normalizedTotalSeconds / 3600);
  const minutes = Math.floor((normalizedTotalSeconds % 3600) / 60);
  const seconds = normalizedTotalSeconds % 60;

  return [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
};
